// Import react elements
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

// Import context
import { AuthContext } from "global/context/AuthContext";

// Import API calls
import { getAccountLastUpdated } from "api/APIStrichCalls";



// Create account last updated context
export const AccountLastUpdatedContext = createContext();



// AccountLastUpdatedProvider polls lightweight account update markers
export const AccountLastUpdatedProvider = ({ children }) => {

    // Get auth values
    const { token, authReady } = useContext(AuthContext);

    // Store the latest account markers
    const [lastUpdated, setLastUpdated] = useState({});

    // Track whether the first marker load has completed
    const [lastUpdatedReady, setLastUpdatedReady] = useState(false);

    // Prevent overlapping poll calls
    const pollingRef = useRef(false);



    // Fetch the latest account markers
    const refreshLastUpdated = useCallback(async () => {

        // Stop if auth is not ready or there is no token
        if (!authReady || !token) { return; }

        // Stop if a poll is already running
        if (pollingRef.current) { return; }

        // Mark the poll as running
        pollingRef.current = true;

        try {

            // Get the latest markers
            const response = await getAccountLastUpdated(token);

            // Store the markers if the call succeeded
            if (response?.success) {
                setLastUpdated(response.results || {});
                setLastUpdatedReady(true);
            }

        } catch (error) {

            // Log the polling error without breaking the app
            console.error("Account last updated poll error:", error);

        } finally {

            // Mark the poll as finished
            pollingRef.current = false;

        }

    }, [authReady, token]);



    // Start polling while logged in
    useEffect(() => {

        // Clear marker state if auth is not ready or there is no token
        if (!authReady || !token) {
            setLastUpdated({});
            setLastUpdatedReady(false);
            return;
        }

        // Run an immediate poll
        refreshLastUpdated();

        // Poll every 15 seconds
        const interval = setInterval(() => {

            // Do not poll while the tab is hidden
            if (document.hidden) { return; }

            // Refresh the markers
            refreshLastUpdated();

        }, 15000);

        // Clear the interval when auth changes or the component unmounts
        return () => clearInterval(interval);

    }, [authReady, token, refreshLastUpdated]);



    return (

        <AccountLastUpdatedContext.Provider value={{
            lastUpdated,
            lastUpdatedReady,
            refreshLastUpdated
        }}>

            {children}

        </AccountLastUpdatedContext.Provider>

    );

};